import type { Category } from '@erp/shared';

export interface CategoryOption {
  id: string;
  label: string;
}

/** Index the tree by parent id (roots under `null`), each level code-ordered. */
function indexByParent(cats: Category[]): Map<string | null, Category[]> {
  const byParent = new Map<string | null, Category[]>();
  for (const c of cats) {
    const key = c.parentId ?? null;
    const list = byParent.get(key);
    if (list) list.push(c);
    else byParent.set(key, [c]);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.code.localeCompare(b.code));
  return byParent;
}

/** Flatten the category tree into depth-indented picker options (by code). */
export function flattenCategoryOptions(cats: Category[]): CategoryOption[] {
  const byParent = indexByParent(cats);
  const out: CategoryOption[] = [];
  const walk = (c: Category, depth: number): void => {
    out.push({ id: c.id, label: `${'— '.repeat(depth)}${c.name}` });
    for (const k of byParent.get(c.id) ?? []) walk(k, depth + 1);
  };
  for (const r of byParent.get(null) ?? []) walk(r, 0);
  return out;
}

/**
 * Every category a product counts under: its primary one, plus the extra shelves
 * when the `multi_category` module is on.
 *
 * The API always serialises `extraCategoryIds`; whether they mean anything is the
 * module's decision, and it has to be taken the same way on every screen or a
 * card's count stops matching the list behind it.
 */
export function registerCategoryIds(
  item: { categoryId?: string | null; extraCategoryIds?: string[] },
  multiCategoryOn: boolean,
): string[] {
  const ids = item.categoryId ? [item.categoryId] : [];
  if (!multiCategoryOn) return ids;
  for (const id of item.extraCategoryIds ?? []) if (!ids.includes(id)) ids.push(id);
  return ids;
}

/**
 * A category id plus every id beneath it.
 *
 * The server filters `/items?categoryId=` by the whole subtree, so any
 * client-side category filter has to do the same or the list contradicts the
 * count that linked to it. The visited set isn't paranoia: nothing locks the
 * tree while two parent edits race, so a saved cycle is reachable and an
 * unguarded walk would never return.
 */
export function categoryDescendantIds(cats: Category[], rootId: string): Set<string> {
  const byParent = indexByParent(cats);
  const out = new Set<string>([rootId]);
  const walk = (id: string): void => {
    for (const k of byParent.get(id) ?? []) {
      if (out.has(k.id)) continue;
      out.add(k.id);
      walk(k.id);
    }
  };
  walk(rootId);
  return out;
}

/**
 * Roll per-category tallies up the tree: each category reports its own items
 * PLUS everything filed underneath it.
 *
 * Sums across rows therefore exceed the catalogue size — that is what "shop the
 * whole department" means, and it's the only tally that agrees with the item
 * list the count links to. Keyed by category id; categories absent from `own`
 * with no children come back as 0.
 */
export function categorySubtreeCounts(
  cats: Category[],
  own: ReadonlyMap<string, number>,
): Map<string, number> {
  const byParent = indexByParent(cats);
  const out = new Map<string, number>();
  const onStack = new Set<string>();
  const walk = (c: Category): number => {
    const done = out.get(c.id);
    if (done !== undefined) return done;
    if (onStack.has(c.id)) return 0; // cycle guard, as in categoryDescendantIds
    onStack.add(c.id);
    let n = own.get(c.id) ?? 0;
    for (const k of byParent.get(c.id) ?? []) n += walk(k);
    onStack.delete(c.id);
    out.set(c.id, n);
    return n;
  };
  // Every category, not just the roots — a category whose parentId points at a
  // row we can't see is invisible in the tree but still needs its own count.
  for (const c of cats) walk(c);
  return out;
}

/** One option being built: an English name + values, and their Arabic pair. */
export type OptionGroupDraft = { name: string; values: string; nameAr: string; valuesAr: string };

/** How many values one AI translate press will send. Each is its own request. */
export const AI_TRANSLATE_MAX = 25;

/**
 * Split the comma-separated values typed into an option.
 *
 * Accepts the ARABIC comma and newlines too. The Arabic placeholder on this
 * form reads «أحمر، أزرق، أخضر», and splitting on "," alone turned that whole
 * line into ONE value — so the Arabic labels never lined up with the English
 * ones and every variant silently fell back to its English name.
 *
 * `keepHoles` preserves empty slots, which the Arabic side needs: values pair
 * to English BY POSITION, so a skipped one has to stay a gap.
 */
export function splitOptionValues(raw: string, keepHoles = false): string[] {
  const parts = raw.split(/[,،؛;\n]/).map((v) => v.trim());
  return keepHoles ? parts : parts.filter(Boolean);
}

/**
 * Rebuild the options builder from the variants a product already has.
 *
 * Editing used to open on an EMPTY builder beside a full list of variants, so
 * adding one colour meant retyping every option from memory — and a typo
 * regenerated the whole list under different names, losing the prices and
 * barcodes attached to the old ones. Variant names are built from the option
 * VALUES joined by " / " (the group names are decorative — they are never
 * saved), so the values can be read back out of the names.
 *
 * Gives up and returns null unless the variants form a COMPLETE grid: the same
 * number of segments in every name, no blank segments, and a cartesian product
 * that comes back to exactly the number of rows on the product. Seeding from a
 * partial list would hand the shop a builder whose Generate quietly resurrects
 * combinations they had deleted.
 */
export function optionGroupsFromVariants(
  variants: ReadonlyArray<{ name: string; nameAr?: string | null }>,
): OptionGroupDraft[] | null {
  const rows = variants.filter((v) => v.name.trim());
  if (rows.length === 0) return null;

  const en = rows.map((v) => v.name.split(' / ').map((s) => s.trim()));
  const width = en[0]?.length ?? 0;
  if (width === 0 || en.some((p) => p.length !== width || p.some((s) => !s))) return null;
  const ar = rows.map((v) => (v.nameAr ?? '').split(' / ').map((s) => s.trim()));

  const groups: OptionGroupDraft[] = [];
  let combos = 1;
  for (let k = 0; k < width; k += 1) {
    // Insertion order, so the rebuilt values read in the order they were made.
    const seen = new Map<string, string>();
    en.forEach((parts, ri) => {
      const value = parts[k] as string;
      if (seen.has(value)) return;
      const arParts = ar[ri];
      seen.set(value, arParts && arParts.length === width ? arParts[k] ?? '' : '');
    });
    combos *= seen.size;
    groups.push({
      name: '',
      nameAr: '',
      values: [...seen.keys()].join(', '),
      valuesAr: [...seen.values()].join('، '),
    });
  }
  return combos === rows.length ? groups : null;
}
